import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  IconButton,
  Grid,
  Slider
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  PlayArrow as PlayIcon,
  Share as ShareIcon,
  QrCode as QrCodeIcon,
  MusicNote as MusicNoteIcon,
  VolumeUp as VolumeUpIcon
} from '@mui/icons-material';
import YoutubeVideoPlayer from '../components/YoutubeVideoPlayer';
import customYoutubeApi from '../api/customYoutubeApi';
import midiPlayer from '../utils/simpleMidiPlayer';

const CustomYoutubeVideoViewer = () => {
  const { customVideoId } = useParams();
  const navigate = useNavigate();
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [thumbnailLoading, setThumbnailLoading] = useState(true);
  const [midiLoading, setMidiLoading] = useState(false);
  const [midiVolume, setMidiVolume] = useState(100);

  // Initialize MIDI player on mount
  useEffect(() => {
    midiPlayer.initialize().catch(err => {
      console.error('Failed to initialize MIDI player:', err);
    });
    
    // Load current volume from MIDI player
    setMidiVolume(midiPlayer.getVolume());

    return () => {
      // Cleanup on unmount
      midiPlayer.stopAll();
    };
  }, []);

  useEffect(() => {
    if (customVideoId) {
      loadVideo();
    }
  }, [customVideoId]);

  const checkBestThumbnail = async (videoId) => {
    const maxResUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
    const hqUrl = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
    
    try {
      // Try maxresdefault first
      const response = await fetch(maxResUrl, { method: 'HEAD' });
      if (response.ok) {
        return maxResUrl;
      }
    } catch (error) {
      console.log(`Maxres thumbnail not available for video ${videoId}`);
    }
    
    // Fallback to hqdefault
    return hqUrl;
  };

  const loadVideo = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await customYoutubeApi.getByCustomId(customVideoId);
      const videoData = response.data;
      setVideo(videoData);
      
      // Load the best available thumbnail
      setThumbnailLoading(true);
      const bestThumbnail = await checkBestThumbnail(videoData.videoId);
      setThumbnailUrl(bestThumbnail);
      setThumbnailLoading(false);
      
    } catch (err) {
      console.error('Error loading video:', err);
      if (err.response?.status === 404) {
        setError('Video not found. It may have been removed or is no longer available.');
      } else {
        setError(`Failed to load video: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: video.title,
          text: video.description || `Watch "${video.title}"`,
          url: url
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          // Fallback to clipboard
          copyToClipboard(url);
        }
      }
    } else {
      copyToClipboard(url);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Link copied to clipboard!');
    }).catch(() => {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('Link copied to clipboard!');
    });
  };

  const handleShowQR = () => {
    navigate(`/youtube-video/${customVideoId}/qr`);
  };

  const handlePlayMidiNote = async (noteName) => {
    if (!noteName) return;
    
    setMidiLoading(true);
    try {
      await midiPlayer.playNote(noteName, 0.5); // Play for 0.5 seconds with current volume
    } catch (error) {
      console.error('Error playing MIDI note:', error);
    } finally {
      setTimeout(() => setMidiLoading(false), 500); // Reset after note duration
    }
  };

  const handleVolumeChange = (event, newValue) => {
    setMidiVolume(newValue);
    midiPlayer.setVolume(newValue);
  };

  // Convert note name (e.g., "C4", "D#5") to MIDI note number
  const noteToMidiValue = (noteName) => {
    if (!noteName) return '';
    
    const noteMap = {
      'C': 0, 'C#': 1, 'Db': 1, 'D': 2, 'D#': 3, 'Eb': 3,
      'E': 4, 'F': 5, 'F#': 6, 'Gb': 6, 'G': 7, 'G#': 8,
      'Ab': 8, 'A': 9, 'A#': 10, 'Bb': 10, 'B': 11
    };
    
    // Extract note and octave (e.g., "C4" -> note="C", octave=4)
    const match = noteName.match(/^([A-G][#b]?)([0-9])$/);
    if (!match) return '';
    
    const note = match[1];
    const octave = parseInt(match[2]);
    
    if (noteMap[note] === undefined) return '';
    
    // MIDI note number = (octave + 1) * 12 + note offset
    // C4 (middle C) = 60
    const midiValue = (octave + 1) * 12 + noteMap[note];
    return midiValue;
  };

  const formatClockTime = (seconds) => {
    const totalSeconds = Math.max(0, Math.floor(seconds));
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '50vh',
        p: 3 
      }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading video...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/custom-youtube-videos')}
          sx={{ mb: 2 }}
        >
          Back to Videos
        </Button>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button 
          variant="outlined" 
          onClick={loadVideo}
          sx={{ mr: 1 }}
        >
          Try Again
        </Button>
        <Button 
          variant="contained" 
          onClick={() => navigate('/custom-youtube-videos')}
        >
          Browse Other Videos
        </Button>
      </Box>
    );
  }

  if (!video) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h5">Video not found</Typography>
        <Button 
          variant="contained" 
          onClick={() => navigate('/custom-youtube-videos')}
          sx={{ mt: 2 }}
        >
          Browse Videos
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/custom-youtube-videos')}
          sx={{ mr: 2 }}
        >
          Back to Videos
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <IconButton onClick={handleShare} title="Share Video">
          <ShareIcon />
        </IconButton>
        <IconButton onClick={handleShowQR} title="Show QR Code">
          <QrCodeIcon />
        </IconButton>
      </Box>

      <Grid container spacing={3}>
        {/* Video Player */}
        <Grid item xs={12} lg={8}>
          <Card sx={{ mb: 2 }}>
            <Box sx={{ position: 'relative' }}>
              {!isPlaying ? (
                <Box 
                  sx={{
                    position: 'relative',
                    width: '100%',
                    paddingBottom: '56.25%', // 16:9 aspect ratio
                    backgroundColor: '#000',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden'
                  }}
                  onClick={() => setIsPlaying(true)}
                >
                  {/* Thumbnail Image */}
                  {thumbnailUrl && (
                    <img 
                      src={thumbnailUrl}
                      alt={video.title || video.name}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        backgroundColor: '#f5f5f5'
                      }}
                      onError={(e) => {
                        console.log(`Thumbnail failed to load for ${video.title || video.name}`);
                        // Fallback to a placeholder if even the fallback thumbnail fails
                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIwIiBoZWlnaHQ9IjE4MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjY2NjIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNHB4IiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+Tm8gSW1hZ2U8L3RleHQ+PC9zdmc+';
                      }}
                    />
                  )}
                  
                  {/* Loading placeholder */}
                  {thumbnailLoading && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        backgroundColor: '#f5f5f5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Loading thumbnail...
                      </Typography>
                    </Box>
                  )}
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      backgroundColor: 'rgba(0, 0, 0, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <IconButton
                      sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        color: '#ff0000',
                        width: 80,
                        height: 80,
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 1)',
                        }
                      }}
                    >
                      <PlayIcon sx={{ fontSize: 40 }} />
                    </IconButton>
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 16,
                      left: 16,
                      right: 16,
                      color: 'white',
                      textShadow: '0 1px 2px rgba(0,0,0,0.8)'
                    }}
                  >
                    <Typography variant="h6" noWrap>
                      {video.title}
                    </Typography>
                    <Typography variant="body2">
                      Custom playback: {video.startPoint}s - {video.endPoint1}s
                      {video.endPoint2 !== undefined && video.endPoint2 !== null && ` - ${video.endPoint2}s`}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <YoutubeVideoPlayer
                  video={video}
                  onBack={() => setIsPlaying(false)}
                  showBackButton={false}
                />
              )}
            </Box>
          </Card>
        </Grid>

        {/* Video Information */}
        <Grid item xs={12} lg={4}>
          <Card>
            <CardContent>
              <Typography variant="h5" component="h1" gutterBottom>
                {video.title}
              </Typography>
              
              {video.description && (
                <>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {video.description}
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                </>
              )}

              {/* Video Details */}
              <Typography variant="h6" gutterBottom>
                📹 Video Details
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  YouTube Video ID:
                </Typography>
                <Typography variant="body1" fontFamily="monospace">
                  {video.videoId}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Custom Video ID:
                </Typography>
                <Typography variant="body1" fontFamily="monospace" fontSize="0.85em">
                  {video.customVideoId}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Playback Timing:
                </Typography>
                <Typography variant="body1">
                  Start: {video.startPoint}s<br />
                  End Point 1: {video.endPoint1}s<br />
                  {video.endPoint2 !== undefined && video.endPoint2 !== null && (
                    <>End Point 2: {video.endPoint2}s</>
                  )}
                  {(video.endPoint2 === undefined || video.endPoint2 === null) && (
                    <Typography variant="caption" color="text.secondary">(Single endpoint mode)</Typography>
                  )}
                </Typography>
              </Box>

              {video.tags && video.tags.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Tags:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {video.tags.map((tag) => (
                      <Chip key={tag} label={tag} size="small" />
                    ))}
                  </Box>
                </Box>
              )}

              {/* MIDI Notes */}
              {(video.midiNote1 || video.midiNote2) && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    🎵 MIDI Notes:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    {video.midiNote1 && (
                      <Chip 
                        label={`${video.midiNote1} (MIDI ${noteToMidiValue(video.midiNote1)})`} 
                        size="small" 
                        color="primary"
                        onClick={() => handlePlayMidiNote(video.midiNote1)}
                        sx={{ cursor: 'pointer' }}
                      />
                    )}
                    {video.midiNote2 && (
                      <Chip 
                        label={`${video.midiNote2} (MIDI ${noteToMidiValue(video.midiNote2)})`} 
                        size="small" 
                        color="error"
                        onClick={() => handlePlayMidiNote(video.midiNote2)}
                        sx={{ cursor: 'pointer' }}
                      />
                    )}
                  </Box>
                  {/* MIDI Volume Control */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <VolumeUpIcon fontSize="small" color="action" />
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60 }}>
                      Volume:
                    </Typography>
                    <Slider
                      value={midiVolume}
                      onChange={handleVolumeChange}
                      min={0}
                      max={127}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(value) => `${Math.round((value / 127) * 100)}%`}
                      size="small"
                      sx={{ flexGrow: 1, maxWidth: 200 }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 35 }}>
                      {Math.round((midiVolume / 127) * 100)}%
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Tonal Key Sections */}
              {video.tonalKeySections && video.tonalKeySections.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    🎼 Tonal Key Sections:
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {video.tonalKeySections.map((section, index) => {
                      const sectionColor = section.color || 'blue';
                      const colorMap = {
                        'blue': '#1976d2',
                        'red': '#d32f2f',
                        'green': '#2e7d32'
                      };
                      return (
                        <Box 
                          key={index} 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                            pl: 1,
                            py: 0.5,
                            borderLeft: `4px solid ${colorMap[sectionColor] || colorMap.blue}`,
                            bgcolor: `${colorMap[sectionColor] || colorMap.blue}15`
                          }}
                        >
                          <Typography variant="body2">
                            {formatClockTime(section.startTime)} - {formatClockTime(section.endTime)}: <strong>{section.key}</strong>
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Actions */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<PlayIcon />}
                  onClick={() => setIsPlaying(true)}
                  disabled={isPlaying}
                >
                  {isPlaying ? 'Playing...' : 'Play Custom Video'}
                </Button>
                
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => window.open(`https://www.youtube.com/watch?v=${video.videoId}`, '_blank')}
                >
                  View on YouTube
                </Button>

                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<ShareIcon />}
                  onClick={handleShare}
                >
                  Share Video
                </Button>

                {/* MIDI Note Buttons */}
                {video.midiNote1 && (
                  <Button
                    variant="outlined"
                    fullWidth
                    color="primary"
                    startIcon={<MusicNoteIcon />}
                    onClick={() => handlePlayMidiNote(video.midiNote1)}
                    disabled={midiLoading}
                  >
                    Play MIDI Note 1: {video.midiNote1}
                  </Button>
                )}

                {video.midiNote2 && (
                  <Button
                    variant="outlined"
                    fullWidth
                    color="secondary"
                    startIcon={<MusicNoteIcon />}
                    onClick={() => handlePlayMidiNote(video.midiNote2)}
                    disabled={midiLoading}
                  >
                    Play MIDI Note 2: {video.midiNote2}
                  </Button>
                )}

                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<QrCodeIcon />}
                  onClick={handleShowQR}
                >
                  Show QR Code
                </Button>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Metadata */}
              <Typography variant="caption" color="text.secondary" display="block">
                Created: {new Date(video.createdAt).toLocaleString()}
              </Typography>
              {video.userName && (
                <Typography variant="caption" color="text.secondary" display="block">
                  By: {video.userName}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CustomYoutubeVideoViewer;